import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CalendarCheck, ArrowLeft, MapPin, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { PageHead, VerifyText } from "@/components/Layout";
import { PricingCards } from "@/components/PricingCards";
import { NewsletterForm } from "@/components/NewsletterForm";
import { useT } from "@/i18n";

export function PricingPage() {
  return (
    <div data-testid="pricing-page">
      <PageHead eyebrow="Pricing" title="Pay for what saves you time." sub="Start free. Upgrade when you're writing for more guests than you'd like. Cancel any time." />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24"><PricingCards /></section>
    </div>
  );
}

export function GuidesPage() {
  const { t, lang } = useT();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/guides").then((r) => setD(r.data)); }, [lang]);
  return (
    <div data-testid="guides-page">
      <PageHead eyebrow="Guides" title="Rules, registration and writing, country by country." sub="Practical guides with a last-reviewed date on every page. Legal claims are marked so you know what to double-check." />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
        <h2 className="text-2xl sm:text-3xl text-[#0F4C5C] mb-8">{t("Country hubs")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {d?.countries.map((c, i) => (
            <motion.div key={c.slug} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <Link to={`/guides/country/${c.slug}`} className="card card-hover p-6 block h-full" data-testid={`country-card-${c.slug}`}>
                <MapPin size={22} weight="duotone" className="text-[#E36414]" />
                <h3 className="text-2xl text-[#0F4C5C] mt-3">{c.name}</h3>
                <p className="text-sm text-gray-700 mt-2">{c.summary}</p>
                <p className="text-xs text-gray-500 mt-4 flex items-center gap-1"><CalendarCheck size={14} /> {t("Last reviewed")} {c.reviewed}</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="regions-section">
        <h2 className="text-2xl sm:text-3xl text-[#0F4C5C] mb-2">{t("Italian regions")}</h2>
        <p className="text-gray-700 mb-8 text-sm">{t("CIN, regional codes and tourist tax in the regions hosts ask us about most.")} <Link to="/tourist-tax" className="text-[#E36414] font-semibold hover:underline" data-testid="guides-tax-calc-link">{t("Tourist tax calculator →")}</Link></p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {d?.regions?.map((c) => (
            <Link key={c.slug} to={`/guides/country/${c.slug}`} className="card card-hover p-6 block" data-testid={`region-card-${c.slug}`}>
              <h3 className="text-xl text-[#0F4C5C]">{c.name}</h3>
              <p className="text-sm text-gray-700 mt-2 line-clamp-3">{c.summary}</p>
              <p className="text-xs text-gray-500 mt-4 flex items-center gap-1"><CalendarCheck size={14} /> {t("Last reviewed")} {c.reviewed}</p>
            </Link>
          ))}
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <h2 className="text-2xl sm:text-3xl text-[#0F4C5C] mb-8">{t("Latest guides")}</h2>
        <div className="divide-y divide-[#E2DCD0] border-y border-[#E2DCD0]">
          {d?.articles.map((a) => (
            <Link key={a.slug} to={`/guides/${a.slug}`} className="py-6 grid md:grid-cols-[1fr_auto] gap-2 group" data-testid={`article-link-${a.slug}`}>
              <div>
                <h3 className="text-xl text-[#0F4C5C] group-hover:text-[#E36414] transition-colors">{a.title}</h3>
                <p className="text-gray-700 text-sm mt-1">{a.excerpt}</p>
              </div>
              <span className="text-xs text-gray-500 flex items-center gap-1"><Clock size={14} /> {a.minutes} min · {a.reviewed}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

const GuideBody = ({ kind }) => {
  const { t, lang } = useT();
  const { slug } = useParams();
  const [g, setG] = useState(null);
  useEffect(() => { api.get(`/guides/${kind}/${slug}`).then((r) => setG(r.data)).catch(() => setG(false)); }, [kind, slug, lang]);
  if (g === false) return <p className="p-16">{t("Guide not found.")}</p>;
  if (!g) return <div className="p-16"><div className="skeleton h-64 max-w-3xl" /></div>;
  const sections = g.sections || g.body;
  return (
    <article className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1fr_320px] gap-12" data-testid="guide-detail-page">
      <div className="max-w-3xl">
        <Link to="/guides" className="text-sm text-[#0F4C5C] inline-flex items-center gap-1 hover:text-[#E36414]" data-testid="guide-back-link"><ArrowLeft size={14} /> {t("All guides")}</Link>
        <h1 className="mt-6 text-4xl sm:text-5xl text-[#0F4C5C]" data-testid="guide-title">{g.name ? t("Short-term rental rules in {name}", { name: g.name }) : g.title}</h1>
        <p className="mt-4 inline-flex items-center gap-2 text-sm bg-white border border-[#E2DCD0] rounded-full px-3 py-1" data-testid="guide-reviewed-date"><CalendarCheck size={16} className="text-[#0F4C5C]" /> {t("Last reviewed")} {g.reviewed}</p>
        {g.summary && <p className="mt-6 text-lg text-gray-700">{g.summary}</p>}
        {g.regions?.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="guide-region-links">
            <span className="text-sm text-gray-600">{t("Regional guides:")}</span>
            {g.regions.map((r) => <Link key={r.slug} to={`/guides/country/${r.slug}`} className="text-sm bg-white border border-[#E2DCD0] rounded-full px-3 py-1 text-[#0F4C5C] hover:text-[#E36414] transition-colors" data-testid={`guide-region-link-${r.slug}`}>{r.name}</Link>)}
          </div>
        )}
        {g.parent && <Link to={`/guides/country/${g.parent}`} className="mt-4 inline-block text-sm text-[#E36414] font-semibold hover:underline" data-testid="guide-parent-link">{t("← National rules for Italy")}</Link>}
        {sections.map((s) => (
          <section key={s.h} className="mt-10">
            <h2 className="text-2xl text-[#0F4C5C]">{s.h}</h2>
            <p className="mt-3 text-gray-800 leading-relaxed"><VerifyText text={s.p} /></p>
          </section>
        ))}
        {g.sources && (
          <div className="mt-10 card p-5">
            <p className="label">{t("Official sources")}</p>
            <ul className="text-sm space-y-1">{g.sources.map((s) => <li key={s}><a href={s} target="_blank" rel="noreferrer" className="text-[#0F4C5C] underline hover:text-[#E36414]">{s}</a></li>)}</ul>
          </div>
        )}
        <p className="mt-8 text-xs text-gray-500 border-l-2 border-[#E36414] pl-3" data-testid="guide-disclaimer">{t("This guide is general information, not legal advice. Rules change and vary by region. Confirm with your local authority before acting.")}</p>
      </div>
      <aside className="space-y-6 lg:sticky lg:top-24 h-fit">
        {g.slug?.startsWith("italy") && (
          <Link to="/tourist-tax" className="card card-hover p-6 block !bg-[#0F4C5C] text-[#F4E9D8]" data-testid="guide-sidebar-tax-card">
            <p className="font-display text-xl">{t("Tourist tax calculator")}</p>
            <p className="text-sm opacity-80 mt-1">{t("Naples, Rome, Florence, Venice: how much to collect, in 10 seconds.")}</p>
          </Link>
        )}
        <div className="card p-6 bg-white">
          <p className="font-display text-xl text-[#0F4C5C]">{t("Is your listing ready?")}</p>
          <p className="text-sm text-gray-600 mt-1">{t("Get a free score and 3 fixes in 20 seconds.")}</p>
          <Link to="/grader" className="btn-coral mt-4 text-sm" data-testid="guide-sidebar-grader-btn">{t("Grade my listing")}</Link>
        </div>
        <div className="card p-6">
          <p className="font-display text-lg text-[#0F4C5C]">{t("Rule changes, by email")}</p>
          <p className="text-sm text-gray-600 mt-1 mb-3">{t("We update country hubs monthly.")}</p>
          <NewsletterForm testid="guide-newsletter" />
        </div>
      </aside>
    </article>
  );
};

export const CountryGuide = () => <GuideBody kind="country" />;
export const ArticleGuide = () => <GuideBody kind="article" />;
